/**
 * Corium 21: Shopify Inbox chat launcher, sitewide (loaded from layout/theme.liquid, after
 * assets/forms-teaser-flag.js).
 *
 * The launcher (<shopify-chat>, its button is part="activator" in the open shadow root) always
 * stays in the bottom-right corner at its resting offset. Nothing here or in
 * assets/sticky-add-to-cart.js lifts or moves it. Instead:
 * - 750px and up: it is shown icon only, the round 48px launcher phones already get (label hidden
 *   through ::part(activator); its aria-label "Chat. Open chat" stays). That keeps it beside the
 *   600px centred sticky add-to-cart bar (at 750px the bar ends at 675 and the icon starts at 682).
 * - Phones: the full-width sticky bar keeps the corner free (right padding, in the sticky JS).
 * - It fades out while it would cover something, and comes back as soon as it doesn't. This
 *   mirrors the 25% off teaser (assets/forms-teaser-flag.js, data-c21-teaser-hide): the same
 *   overlap test with an 8px gap against the launcher's own rect, the same footer and card
 *   selectors, and the same fade. data-c21-chat-hide on <shopify-chat> lists why:
 *     "footer"  a footer link, button, field, icon, list item or image is under it (this is what
 *               reaches the bottom of the page: socials, payment icons, policies)
 *     "end"     the footer's last row (bottom bar) is in view, so it is gone at the page bottom
 *               even where no footer link happens to sit in its corner
 *     "cards"   a "You may also like" product card is under it
 *     "buy"     the product's variant picker or buy buttons block is under it (every width: the
 *               launcher is on the right, where the purchase column is)
 *     "text"    a visible line of page text anywhere in <main> is under it: product details,
 *               Judge.me reviews, and home/page sections (headings, body copy, the review
 *               carousel, video handles). Tested against the text lines themselves, not their
 *               blocks, so it only goes when it would really cover words (at 1440 the description
 *               runs to x=1400 and the launcher starts at x=1368). Text that is transparent or
 *               visibility-hidden (closed accordion panels fade to opacity 0), visually-hidden
 *               (1px boxes), outside its own box (line-clamped "More" bodies) or cut off by an
 *               overflow-clipping ancestor (carousel slides off the track) does not count.
 *               Re-checked when a carousel slide finishes moving (transitionend / animationend),
 *               since autoplay changes what is under the corner without a scroll.
 *   On phones, while the sticky add-to-cart bar is up, the launcher sits inside the bar's right
 *   padding. The page under it is already behind the bar there, so only "end" applies.
 *   Hidden means display: none on <shopify-chat>. Fading the button alone (visibility /
 *   pointer-events, as the teaser does) is not enough here: the Inbox runtime nested inside it
 *   keeps catching taps over the footer's social links. Its last visible rect is kept for the
 *   overlap test while it is hidden, and it is never hidden while the chat window is open.
 */
(() => {
  if (window.__c21ChatDock) return;
  window.__c21ChatDock = true;
  const STYLE_ID = 'c21-chat-dock-styles';
  if (!document.getElementById(STYLE_ID)) {
    const style = document.createElement('style');
    style.id = STYLE_ID;
    style.textContent = `
@media screen and (min-width: 750px) {
  shopify-chat::part(activator) {
    font-size: 0;
    gap: 0;
  }
}
shopify-chat[data-c21-chat-hide] {
  display: none !important;
}
`;
    document.head.append(style);
  }

  const GAP_PX = 8;
  const FOOTER = '.shopify-section-group-footer-group :is(a, button, input, svg, li, img)';
  const CARDS = 'main product-recommendations product-card';
  const BUY = 'main variant-picker, main .buy-buttons-block';
  const TEXT = 'main';
  const MOBILE = window.matchMedia('(max-width: 749px)');

  const overlaps = (a, b) =>
    a.left - GAP_PX < b.right && a.right + GAP_PX > b.left &&
    a.top - GAP_PX < b.bottom && a.bottom + GAP_PX > b.top;

  const covered = (ar, selector) => {
    for (const e of document.querySelectorAll(selector)) {
      const r = e.getBoundingClientRect();
      if (r.width && overlaps(ar, r)) return true;
    }
    return false;
  };

  /** True while a visible text line inside <main> is under the launcher. */
  const textCovered = (ar) => {
    for (const root of document.querySelectorAll(TEXT)) {
      const rr = root.getBoundingClientRect();
      if (!rr.width || !overlaps(ar, rr)) continue;
      const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
      const range = document.createRange();
      let node;
      while ((node = walker.nextNode())) {
        if (!node.textContent.trim()) continue;
        const el = node.parentElement;
        const pr = el && el.getBoundingClientRect();
        if (!pr || pr.width <= 1 || pr.height <= 1 || !overlaps(ar, pr)) continue;
        if (el.checkVisibility && !el.checkVisibility({ opacityProperty: true, visibilityProperty: true })) continue;
        range.selectNodeContents(node);
        for (const r of range.getClientRects()) {
          if (r.width && overlaps(ar, r) && overlaps(ar, clipped(el, r, root))) return true;
        }
      }
    }
    return false;
  };

  /** r cut down to every ancestor (up to root) that clips its overflow, e.g. a carousel track. */
  const clipped = (el, r, root) => {
    let { left, top, right, bottom } = r;
    for (let a = el; a && a !== root.parentElement; a = a.parentElement) {
      const cs = getComputedStyle(a);
      if (cs.overflowX === 'visible' && cs.overflowY === 'visible') continue;
      const b = a.getBoundingClientRect();
      left = Math.max(left, b.left); top = Math.max(top, b.top);
      right = Math.min(right, b.right); bottom = Math.min(bottom, b.bottom);
      if (right <= left || bottom <= top) return { left: 0, top: 0, right: 0, bottom: 0, width: 0 };
    }
    return { left, top, right, bottom, width: right - left };
  };

  /** The launcher button inside the Inbox element (open shadow root, part="activator"). */
  const activatorOf = (chat) => {
    const walk = (root) => {
      for (const el of root.querySelectorAll('*')) {
        if (el.getAttribute('part') === 'activator') return el;
        if (el.shadowRoot) {
          const found = walk(el.shadowRoot);
          if (found) return found;
        }
      }
      return null;
    };
    return chat.shadowRoot ? walk(chat.shadowRoot) : null;
  };

  /** True while the Inbox chat window (anything in its shadow root bigger than the button) is open. */
  const windowOpen = (chat) => {
    const walk = (root) => {
      for (const el of root.querySelectorAll('*')) {
        const r = el.getBoundingClientRect();
        if (r.width > 200 && r.height > 200) return true;
        if (el.shadowRoot && walk(el.shadowRoot)) return true;
      }
      return false;
    };
    return chat.shadowRoot ? walk(chat.shadowRoot) : false;
  };

  /** True while the footer's last row (its bottom bar) is in the viewport. */
  const atEnd = () => {
    const sections = document.querySelectorAll('.shopify-section-group-footer-group');
    const last = sections[sections.length - 1];
    if (!last) return false;
    const r = last.getBoundingClientRect();
    return r.height > 0 && r.top < window.innerHeight && r.bottom > 0;
  };

  /** True while the launcher sits inside the visible sticky add-to-cart bar (phones). */
  const insideBar = (ar) => {
    if (!document.body.hasAttribute('data-sticky-atc-visible')) return false;
    const bar = document.querySelector('sticky-add-to-cart [ref=stickyBar]');
    const r = bar && bar.getBoundingClientRect();
    return !!r && r.width > 0 && ar.left >= r.left && ar.right <= r.right && ar.top >= r.top && ar.bottom <= r.bottom;
  };

  let queued = false;
  let lastRect = null;
  const update = () => {
    queued = false;
    const chat = document.querySelector('shopify-chat');
    if (!chat) return;
    const act = activatorOf(chat);
    let ar = act && act.getBoundingClientRect();
    if (ar && ar.width) lastRect = ar;
    else if (chat.hasAttribute('data-c21-chat-hide')) ar = lastRect;
    const why = [];
    if (ar && ar.width && !windowOpen(chat)) {
      const underBar = insideBar(ar);
      if (!underBar && covered(ar, FOOTER)) why.push('footer');
      if (atEnd()) why.push('end');
      if (!underBar && covered(ar, CARDS)) why.push('cards');
      if (!underBar && covered(ar, BUY)) why.push('buy');
      if (!underBar && textCovered(ar)) why.push('text');
    }
    const hide = why.join(' ');
    if (hide) {
      if (chat.getAttribute('data-c21-chat-hide') !== hide) chat.setAttribute('data-c21-chat-hide', hide);
    } else if (chat.hasAttribute('data-c21-chat-hide')) {
      chat.removeAttribute('data-c21-chat-hide');
    }
  };
  const apply = () => {
    if (queued) return;
    queued = true;
    requestAnimationFrame(update);
  };

  // capture: at desktop widths Canvas scrolls div.page-wrapper, not the window, and element
  // scroll events don't bubble
  document.addEventListener('scroll', apply, { passive: true, capture: true });
  // carousels (Judge.me reviews, UGC videos) move text under the corner without a scroll
  document.addEventListener('transitionend', apply, { passive: true, capture: true });
  document.addEventListener('animationend', apply, { passive: true, capture: true });
  // a resize can move the corner: show it for one frame so its rect is measured afresh
  addEventListener('resize', () => {
    lastRect = null;
    document.querySelector('shopify-chat')?.removeAttribute('data-c21-chat-hide');
    apply();
  });
  MOBILE.addEventListener('change', apply);
  // the sticky add-to-cart bar, lazy recommendations and late widgets move things without a scroll
  // (again once the bar has finished sliding in or out)
  new MutationObserver(() => {
    apply();
    setTimeout(apply, 450);
  }).observe(document.body, { attributes: true, attributeFilter: ['data-sticky-atc-visible'] });
  const ro = new ResizeObserver(apply);
  ro.observe(document.documentElement);
  ro.observe(document.body);
  const main = document.querySelector('main');
  if (main) ro.observe(main);
  customElements.whenDefined('shopify-chat').then(() => setTimeout(apply, 500));
  apply();
})();
